// @ts-nocheck
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useDebounce } from '@/hooks/useDebounce';
import { toast } from 'sonner';
import { Check, X, Loader2, Upload, Image as ImageIcon, Save } from 'lucide-react';

const SUBDOMAIN_REASONS: Record<string, string> = {
  empty: 'Enter a subdomain',
  length: 'Must be 3–63 characters',
  format: 'Only lowercase letters, numbers, and hyphens',
  reserved: 'That subdomain is reserved',
  taken: 'Already taken',
};

const SAVE_ERRORS: Record<string, string> = {
  not_authorized: 'You are not authorized to update this organization.',
  subdomain_taken: 'That subdomain is already taken.',
  subdomain_reserved: 'That subdomain is reserved.',
  subdomain_format: 'Subdomain can only contain lowercase letters, numbers, and hyphens.',
  subdomain_length: 'Subdomain must be 3–63 characters.',
  subdomain_empty: 'Please enter a subdomain.',
};

const MAX_LOGO_BYTES = 2 * 1024 * 1024; // 2MB
const ACCEPTED = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];

const OrgBrandingSettings: React.FC = () => {
  const { profile } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const [orgId, setOrgId] = useState<string | null>(null);

  const [subdomain, setSubdomain] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [isRecruitlookBranded, setIsRecruitlookBranded] = useState(true);

  const [checkState, setCheckState] = useState<{ status: 'idle' | 'checking' | 'available' | 'error'; message?: string }>({ status: 'idle' });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const debouncedSubdomain = useDebounce(subdomain, 450);

  // Resolve org + load current branding
  useEffect(() => {
    const load = async () => {
      if (!profile?.id) return;
      try {
        setLoading(true);
        const { data: member } = await (supabase as any)
          .from('organization_members')
          .select('organization_id, role')
          .eq('user_id', profile.id)
          .eq('status', 'active')
          .in('role', ['owner', 'admin', 'org_admin'])
          .maybeSingle();

        if (!member?.organization_id) {
          setAuthorized(false);
          setLoading(false);
          return;
        }

        setAuthorized(true);
        setOrgId(member.organization_id);

        const { data: org } = await (supabase as any)
          .from('organizations')
          .select('subdomain, logo_url, is_recruitlook_branded')
          .eq('id', member.organization_id)
          .single();

        if (org) {
          setSubdomain(org.subdomain || '');
          setLogoUrl(org.logo_url || '');
          setIsRecruitlookBranded(org.is_recruitlook_branded ?? true);
        }
      } catch (e) {
        console.error('Failed to load branding settings', e);
        toast.error('Failed to load branding settings');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [profile?.id]);

  // Live subdomain validation
  useEffect(() => {
    const check = async () => {
      const v = debouncedSubdomain.trim();
      if (!v) {
        setCheckState({ status: 'idle' });
        return;
      }
      if (!orgId) return;
      setCheckState({ status: 'checking' });
      try {
        const { data, error } = await (supabase as any).rpc('check_subdomain_available', {
          p_subdomain: v,
          p_org_id: orgId,
        });
        if (error) throw error;
        if (data?.available) {
          setCheckState({ status: 'available', message: 'Available' });
        } else {
          setCheckState({ status: 'error', message: SUBDOMAIN_REASONS[data?.reason] || 'Not available' });
        }
      } catch (e) {
        console.error('Subdomain check failed', e);
        setCheckState({ status: 'error', message: 'Could not check availability' });
      }
    };
    check();
  }, [debouncedSubdomain, orgId]);

  const handleClearSubdomain = () => {
    setSubdomain('');
    setCheckState({ status: 'idle' });
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !orgId) return;

    if (!ACCEPTED.includes(file.type)) {
      toast.error('Please upload a PNG, JPG, SVG, or WebP image');
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      toast.error('Logo must be 2MB or smaller');
      return;
    }

    try {
      setUploading(true);
      const ext = (file.name.split('.').pop() || 'png').toLowerCase();
      const path = `${orgId}/logo.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('org-logos')
        .upload(path, file, { cacheControl: '3600', upsert: true });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage.from('org-logos').getPublicUrl(path);
      // cache-bust so the new logo shows immediately
      setLogoUrl(`${urlData.publicUrl}?t=${Date.now()}`);
      toast.success('Logo uploaded — remember to save');
    } catch (e: any) {
      console.error('Logo upload failed', e);
      toast.error(e.message || 'Failed to upload logo');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const subdomainCleared = useMemo(() => subdomain.trim() === '', [subdomain]);

  const handleSave = async () => {
    if (!orgId) return;
    // block save when subdomain present but invalid
    if (!subdomainCleared && checkState.status === 'error') {
      toast.error('Please fix the subdomain before saving');
      return;
    }
    try {
      setSaving(true);
      const { data, error } = await (supabase as any).rpc('save_org_branding', {
        p_org_id: orgId,
        p_subdomain: subdomainCleared ? null : subdomain.trim(),
        p_logo_url: logoUrl ? logoUrl.split('?')[0] : null,
        p_is_recruitlook_branded: isRecruitlookBranded,
        p_clear_subdomain: subdomainCleared,
      });
      if (error) throw error;

      if (!data?.ok) {
        toast.error(SAVE_ERRORS[data?.error] || `Could not save (${data?.error || 'unknown error'})`);
        return;
      }

      const org = data.org || {};
      setSubdomain(org.subdomain || '');
      setLogoUrl(org.logo_url || '');
      setIsRecruitlookBranded(org.is_recruitlook_branded ?? true);
      setCheckState({ status: 'idle' });
      toast.success('Branding saved');
    } catch (e: any) {
      console.error('Save branding failed', e);
      toast.error(e.message || 'Failed to save branding');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!authorized) {
    return (
      <div className="p-4 bg-destructive/10 border-l-4 border-destructive text-destructive">
        <p className="font-bold">Not available</p>
        <p>Only organization admins can manage branding.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold font-heading">Branding</h1>
        <p className="text-muted-foreground mt-1">
          Set a custom subdomain and logo so your events appear under your own brand.
          Custom domains (your own website address) are coming soon.
        </p>
      </div>

      {/* Subdomain */}
      <Card>
        <CardHeader>
          <CardTitle>Subdomain</CardTitle>
          <CardDescription>Choose the web address your event pages will live under.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-stretch gap-0">
            <Input
              value={subdomain}
              onChange={(e) => setSubdomain(e.target.value.toLowerCase())}
              placeholder="your-org"
              className="rounded-r-none"
              autoCapitalize="none"
              spellCheck={false}
            />
            <span className="inline-flex items-center px-3 rounded-r-md border border-l-0 border-input bg-muted text-sm text-muted-foreground whitespace-nowrap">
              .recruitlookevents.com
            </span>
          </div>

          <div className="flex items-center justify-between min-h-[20px]">
            <div className="text-sm">
              {checkState.status === 'checking' && (
                <span className="flex items-center gap-1 text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking…
                </span>
              )}
              {checkState.status === 'available' && (
                <span className="flex items-center gap-1 text-green-600">
                  <Check className="h-3.5 w-3.5" /> {checkState.message}
                </span>
              )}
              {checkState.status === 'error' && (
                <span className="flex items-center gap-1 text-destructive">
                  <X className="h-3.5 w-3.5" /> {checkState.message}
                </span>
              )}
            </div>
            {subdomain.trim() !== '' && (
              <Button type="button" variant="ghost" size="sm" onClick={handleClearSubdomain}>
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Logo */}
      <Card>
        <CardHeader>
          <CardTitle>Logo</CardTitle>
          <CardDescription>PNG, JPG, SVG, or WebP. Max 2MB.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="w-32 h-20 rounded border bg-muted/40 flex items-center justify-center overflow-hidden shrink-0">
              {logoUrl ? (
                <img src={logoUrl} alt="Organization logo" className="max-w-full max-h-full object-contain" />
              ) : (
                <ImageIcon className="h-6 w-6 text-muted-foreground" />
              )}
            </div>
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED.join(',')}
                onChange={handleFileSelect}
                className="hidden"
                id="org-logo-upload"
              />
              <Button
                type="button"
                variant="outline"
                disabled={uploading}
                onClick={() => fileInputRef.current?.click()}
              >
                {uploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading…
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" /> {logoUrl ? 'Replace logo' : 'Upload logo'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Branding toggle */}
      <Card>
        <CardHeader>
          <CardTitle>Branding display</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-start justify-between gap-4">
            <div>
              <Label htmlFor="branding-toggle" className="text-base">
                Show my organization's branding instead of RecruitLook
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                When on, your public event pages show your logo and subdomain. When off, they show RecruitLook branding.
              </p>
            </div>
            <Switch
              id="branding-toggle"
              checked={!isRecruitlookBranded}
              onCheckedChange={(checked) => setIsRecruitlookBranded(!checked)}
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving || uploading}>
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" /> Save branding
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default OrgBrandingSettings;
